import React, { useEffect, useState } from "react";
import Table from "../../../../../components/Table/Table";
import BackButton from "../../../../../components/BackButton/BackButton";
import { toast } from "react-toastify";
import Input from "../../../../../components/Input/Input";
import Modal from "../../../../../components/Modal/Modal";
import Form from "../../../../../components/Form/Form";
import SearchFilterBar from "../components/SearchFilterBar";
import { useSearchParams } from "react-router-dom";
import { useGetAllSchools } from "../../../Dashboard";
import { LoadingSpinner, Pagination } from "./SchoolList";

type FilterOption = {
  value: string;
  name: string;
};

type FiltersState = {
  trustee: FilterOption | null;
  institute: FilterOption | null;
};

function UpdateSchoolDatabase() {
  const [open, set_open] = useState(false);
  const [password, set_password] = useState<any>("");
  const [school, setSchool] = useState<any>({
    id: "",
    name: "",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: schoolsData,
    loading: schoolsLoading,
    error: schoolsError,
    refetch: refetchSchoolsQuery,
  } = useGetAllSchools();

  const initialPage = parseInt(searchParams.get("page") || "1");
  const initialItemsPerRow = parseInt(searchParams.get("limit") || "10");
  const initialSearchText = searchParams.get("search") || "";
  const initialSearchFilter = searchParams.get("filter") || "school_id";

  const [searchText, setSearchText] = useState(initialSearchText);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerRow, setItemsPerRow] = useState(initialItemsPerRow);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localSchoolData, setLocalSchoolData] = useState<any>(null);

  const [filters, setFilters] = useState<FiltersState>({
    trustee: null,
    institute: null,
  });

  const buildRefetchParams = (page: number) => {
    const params: any = {
      page,
      limit: itemsPerRow,
    };

    if (searchText.trim()) {
      if (searchFilter === "school_id") params.school_id = searchText.trim();
      if (searchFilter === "school_name")
        params.school_name = searchText.trim();
      if (searchFilter === "trustee_id") params.trustee_id = searchText.trim();
    }

    if (filters?.trustee && searchFilter !== "trustee_id") {
      params.trustee_id = filters.trustee.value;
    }

    if (filters?.institute && searchFilter !== "school_id") {
      params.school_id = filters.institute.value;
    }

    return params;
  };

  useEffect(() => {
    if (schoolsData?.getallschool) {
      setLocalSchoolData(schoolsData.getallschool);
    }
  }, [schoolsData]);

  useEffect(() => {
    const params: any = {
      page: currentPage.toString(),
      limit: itemsPerRow.toString(),
    };

    if (searchText) {
      params.search = searchText;
      params.filter = searchFilter;
    }

    if (filters.trustee) {
      params.trustee_id = filters.trustee.value;
      params.trustee_name = filters.trustee.name;
    }

    if (filters.institute) {
      params.school_id = filters.institute.value;
      params.school_name = filters.institute.name;
    }

    setSearchParams(params);
  }, [
    currentPage,
    itemsPerRow,
    searchText,
    filters,
    searchFilter,
    setSearchParams,
  ]);

  const getToken = async (id: any, password: any) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("token")
    );

    return await (
      await fetch(
        process.env.REACT_APP_BACKEND_URL +
          `/school-auth/gen-token?school_id=${id}&password=${password}`,
        {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        }
      )
    ).json();
  };

  async function handleLogin(schoolId: any, schoolName: any) {
    if (!password.length) return;

    const res = await getToken(schoolId, password);

    if (!res?.token) {
      return toast.error(`Error While Creating Token For ${schoolName}`, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }

    set_open(false);
    window.open(
      process.env.REACT_APP_SCHOOL_DASHBOARD_URL + "?token=" + res.token,
      "_blank"
    );
  }

  const refetchSchools = async (params?: any) => {
    try {
      setRefetchLoading(true);
      const result = await refetchSchoolsQuery(params || {});
      if (result.data?.getAllSchools) {
        setLocalSchoolData(result.data.getAllSchools);
      }
      return result;
    } finally {
      setRefetchLoading(false);
    }
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    try {
      const params = buildRefetchParams(page);
      await refetchSchools(params);
    } finally {
      setLoading(false);
    }
  };

  if (schoolsError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading school details
      </div>
    );
  }

  const isLoading =
    schoolsLoading || loading || refetchLoading || !localSchoolData;

  return (
    <div className="flex flex-col space-y-4">
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div>
            <BackButton to="/school" label="School Database not Updated" />
            <div>
              <div className="grid grid-cols-6">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>

              <Modal
                title={"Verify Credentials"}
                open={open}
                setOpen={set_open}
              >
                <Form onSubmit={() => handleLogin(school.id, school.name)}>
                  <Input
                    type="password"
                    name={"Password"}
                    placeholder={"Enter Master Admin Password"}
                    add_error={() => {}}
                    onChange={(name: any, e: any) => {
                      set_password(e.target.value);
                    }}
                    required
                  />

                  <div className="flex justify-center">
                    <button
                      disabled={!password.length}
                      className="py-2 px-10 disabled:bg-green-300 bg-green-500 text-white rounded-lg"
                    >
                      Verify
                    </button>
                  </div>
                </Form>
              </Modal>

              <Table
                loading={isLoading}
                searchBox={
                  <SearchFilterBar
                    refetchSchools={refetchSchools}
                    itemsPerRow={itemsPerRow}
                    setItemsPerRow={setItemsPerRow}
                    setCurrentPage={setCurrentPage}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    searchFilter={searchFilter}
                    setSearchFilter={setSearchFilter}
                    filters={filters}
                    setFilters={setFilters}
                    setLoading={setLoading}
                    refetchLoading={refetchLoading}
                    setRefetchLoading={setRefetchLoading}
                    showContainerBorder={false}
                  />
                }
                footer={(() => {
                  const totalItems = localSchoolData.pagination?.total;
                  const totalPages = Math.ceil(totalItems / itemsPerRow);

                  return totalPages > 1 ? (
                    <div className="mt-6 flex justify-center items-center">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  ) : null;
                })()}
                data={[
                  [
                    "School Name",
                    "School City",
                    "Last Updated",
                    "School ID",
                    "Action",
                    "Status",
                  ],
                  ...localSchoolData.data.map((school: any) => [
                    <div>{school.name}</div>,
                    <div>{school?.address?.city}</div>,
                    <div>{new Date(school?.updatedAt).toDateString()}</div>,
                    <div></div>,
                    <div>
                      <button
                        onClick={() => {
                          setSchool({
                            id: school._id,
                            name: school.name,
                          });
                          set_open(true);
                        }}
                        className="bg-indigo-500 text-white px-4 py-1.5 rounded-lg"
                      >
                        Login to School
                      </button>
                    </div>,
                    <div>To do</div>,
                  ]),
                ]}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UpdateSchoolDatabase;
